import { z } from "zod";
import type { GameRouteScope } from "@softmax/web-addresses";
import { buildHref } from "@softmax/web-addresses";
export const resourceSearchSchema = z.record(
  z.string(),
  z.union([z.string(), z.array(z.string())]).optional(),
);
export type ResourceSearch = z.infer<typeof resourceSearchSchema>;
export type ResourceInput = {
  resourceId: string;
  game?: GameRouteScope | undefined;
  search: ResourceSearch;
};
export type PublicDetailInput = Omit<ResourceInput, "game">;
export type ReplayInput =
  | (ResourceInput & { target: "episode"; game: GameRouteScope })
  | (ResourceInput & { target: "coworld" });
export type ResourceDestination =
  | {
      readonly name: "episode";
      readonly episodeId: string;
    }
  | {
      readonly name: "episodeReplay";
      readonly game: GameRouteScope;
      readonly episodeId: string;
    }
  | {
      readonly name: "requestWatch";
      readonly requestId: string;
    }
  | {
      readonly name: "lobby";
      readonly lobbyId: string;
    }
  | {
      readonly name: "playSession";
      readonly sessionId: string;
    }
  | {
      readonly name: "replay";
      readonly coworldId: string;
    };
export function resourcePath({
  input,
  destination,
}: {
  readonly input: Pick<ResourceInput, "game">;
  readonly destination: ResourceDestination;
}): string {
  if (destination.name === "replay" || destination.name === "episodeReplay") {
    return buildHref(destination);
  }

  return buildHref({ ...destination, game: input.game });
}
export function resourceHref({
  input,
  destination,
}: {
  readonly input: Pick<ResourceInput, "game" | "search">;
  readonly destination: ResourceDestination;
}): string {
  return appendResourceSearch({
    href: resourcePath({
      input,
      destination,
    }),
    search: input.search,
  });
}
export function appendResourceSearch({
  href,
  search,
}: {
  readonly href: string;
  readonly search: ResourceSearch;
}): string {
  const url = new URL(href, "https://softmax.invalid");

  for (const [name, value] of Object.entries(search)) {
    if (value === undefined || url.searchParams.has(name)) continue;

    for (const item of Array.isArray(value) ? value : [value]) {
      url.searchParams.append(name, item);
    }
  }

  return `${url.pathname}${url.search}${url.hash}`;
}
